import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, Video, MapPin, Globe2, Loader2, Save } from "lucide-react";
import { coachDashApi, DASH_TESTIDS as D } from "@/lib/coachDashApi";
import {
  SESSION_DURATION_CHOICES,
  TIMEZONE_CHOICES,
  WEEKDAY_LONG,
  WEEKDAY_SHORT,
  tzOffsetLabel,
} from "@/lib/sessionFormat";

// -----------------------------------------------------------------------------
// AvailabilityEditor
// -----------------------------------------------------------------------------
// Simple MVP UI so coaches can control:
//   - session modes (online, in-person)
//   - session duration
//   - price per session
//   - timezone
//   - per-weekday open windows (single window per weekday for MVP)
// -----------------------------------------------------------------------------
export default function AvailabilityEditor({ coachEmail }) {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const a = await coachDashApi.getAvailability(coachEmail);
        if (alive) setAvailability(_hydrate(a));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't load availability.");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [coachEmail]);

  const enabledCount = useMemo(
    () => (availability?.weekly_availability || []).length,
    [availability]
  );

  if (loading || !availability) {
    return (
      <div
        data-testid={D.availabilitySection}
        className="mt-10 bg-white border border-slate-200 rounded-2xl p-6 flex items-center gap-3 text-slate-500 text-[13px]"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your availability…
      </div>
    );
  }

  const toggleMode = (key) => (v) => {
    setAvailability((prev) => ({ ...prev, [key]: v }));
  };

  const toggleDay = (wd) => {
    setAvailability((prev) => {
      const has = prev.weekly_availability.find((w) => w.weekday === wd);
      const rest = prev.weekly_availability.filter((w) => w.weekday !== wd);
      const next = has ? rest : [...rest, { weekday: wd, start_hour: 9, end_hour: 18 }];
      next.sort((a, b) => a.weekday - b.weekday);
      return { ...prev, weekly_availability: next };
    });
  };

  const patchDay = (wd, patch) => {
    setAvailability((prev) => ({
      ...prev,
      weekly_availability: prev.weekly_availability.map((w) =>
        w.weekday === wd ? { ...w, ...patch } : w
      ),
    }));
  };

  const save = async () => {
    // Client-side guard: at least one mode must remain on and each open day
    // must have end > start.
    if (!availability.online_available && !availability.in_person_available) {
      toast.error("Turn on at least one session mode.");
      return;
    }
    for (const w of availability.weekly_availability) {
      if (w.end_hour <= w.start_hour) {
        toast.error(`${WEEKDAY_LONG[w.weekday]} end time must be after the start time.`);
        return;
      }
    }
    setSaving(true);
    try {
      const saved = await coachDashApi.saveAvailability(coachEmail, {
        online_available: availability.online_available,
        in_person_available: availability.in_person_available,
        session_duration_minutes: availability.session_duration_minutes,
        price_per_session: availability.price_per_session,
        timezone: availability.timezone,
        weekly_availability: availability.weekly_availability,
      });
      setAvailability(_hydrate(saved));
      toast.success("Availability saved — clients will see this immediately.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save availability.");
    } finally { setSaving(false); }
  };

  const offset = tzOffsetLabel(availability.timezone);

  return (
    <motion.section
      data-testid={D.availabilitySection}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-10"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h2 className="font-display font-medium text-2xl tracking-tight text-slate-900 inline-flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-slate-500" strokeWidth={2} />
          Availability
        </h2>
        <p className="text-[12.5px] text-slate-500">
          Clients see slots in <span className="font-semibold text-slate-900">their own local time</span>.
          You define hours in yours.
        </p>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6 md:p-7 space-y-8">
        {/* Session modes */}
        <div>
          <div className="eyebrow">§ Session type</div>
          <p className="mt-1 text-[12.5px] text-slate-500">Turn on the way you meet clients.</p>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <ModeToggle
              testId={D.availabilityOnline}
              on={availability.online_available}
              onChange={toggleMode("online_available")}
              Icon={Video}
              title="Online (video call)"
              subtitle="Zoom / Google Meet — link shared before the session."
            />
            <ModeToggle
              testId={D.availabilityInPerson}
              on={availability.in_person_available}
              onChange={toggleMode("in_person_available")}
              Icon={MapPin}
              title="In-person"
              subtitle="Client meets you at an agreed location."
            />
          </div>
        </div>

        {/* Duration + price + timezone */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Session length">
            <select
              data-testid={D.availabilityDuration}
              value={availability.session_duration_minutes}
              onChange={(e) => setAvailability((p) => ({ ...p, session_duration_minutes: parseInt(e.target.value, 10) }))}
              className="mt-2 w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
            >
              {SESSION_DURATION_CHOICES.map((d) => (
                <option key={d} value={d}>{d} minutes</option>
              ))}
            </select>
          </Field>
          <Field label="Price per session (USD)">
            <div className="mt-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[14px]">$</span>
              <input
                data-testid={D.availabilityPrice}
                type="number"
                min={1}
                max={10000}
                value={availability.price_per_session}
                onChange={(e) => setAvailability((p) => ({ ...p, price_per_session: Math.max(1, parseInt(e.target.value || "0", 10) || 0) }))}
                className="w-full h-11 rounded-xl bg-slate-50 border border-slate-200 pl-7 pr-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
              />
            </div>
          </Field>
          <Field label={<span className="inline-flex items-center gap-1"><Globe2 className="w-3.5 h-3.5" /> Your timezone</span>}>
            <select
              data-testid={D.availabilityTimezone}
              value={availability.timezone}
              onChange={(e) => setAvailability((p) => ({ ...p, timezone: e.target.value }))}
              className="mt-2 w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
            >
              {TIMEZONE_CHOICES.map((z) => <option key={z} value={z}>{z}</option>)}
              {!TIMEZONE_CHOICES.includes(availability.timezone) && (
                <option value={availability.timezone}>{availability.timezone}</option>
              )}
            </select>
            {offset && <p className="mt-1 text-[11px] text-slate-400">Currently {offset}</p>}
          </Field>
        </div>

        {/* Weekly windows */}
        <div>
          <div className="eyebrow">§ Weekly hours</div>
          <p className="mt-1 text-[12.5px] text-slate-500">
            Pick the days and hours you're open. Slots are auto-generated in your timezone,
            spaced by your session length.
          </p>

          <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
            {[0, 1, 2, 3, 4, 5, 6].map((wd) => {
              const active = availability.weekly_availability.find((w) => w.weekday === wd);
              const isOpen = !!active;
              // Show time fields for EVERY day so weekends look identical to
              // weekdays — just dimmed when the day is closed. Values shown
              // for a closed day are the last-configured (or default)
              // values so re-opening keeps them.
              const start = active?.start_hour ?? 9;
              const end = active?.end_hour ?? 18;
              return (
                <li key={wd} className="p-3 md:p-4 flex items-center gap-4 flex-wrap">
                  <label className="inline-flex items-center gap-2 min-w-[150px] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={D.availabilityWeekdayToggle(wd)}
                      checked={isOpen}
                      onChange={() => toggleDay(wd)}
                      className="w-4 h-4 accent-[#2563EB]"
                    />
                    <span className="text-[13.5px] font-medium text-slate-900">{WEEKDAY_LONG[wd]}</span>
                  </label>
                  <div
                    className={`flex items-center gap-2 flex-wrap transition-opacity ${
                      isOpen ? "opacity-100" : "opacity-45"
                    }`}
                    aria-hidden={!isOpen}
                  >
                    <HourSelect
                      testId={D.availabilityStart(wd)}
                      value={start}
                      max={23}
                      disabled={!isOpen}
                      onChange={(v) => patchDay(wd, { start_hour: v })}
                    />
                    <span className="text-slate-400 text-[13px]">→</span>
                    <HourSelect
                      testId={D.availabilityEnd(wd)}
                      value={end}
                      min={1}
                      max={24}
                      disabled={!isOpen}
                      onChange={(v) => patchDay(wd, { end_hour: v })}
                    />
                    <span className="text-[11.5px] text-slate-400 ml-2 hidden sm:inline">
                      {isOpen ? availability.timezone : "closed — tick to open"}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>

          <p className="mt-3 text-[11.5px] text-slate-400">
            {enabledCount === 0
              ? "No open days — clients will see no available slots."
              : `Open ${enabledCount} day${enabledCount === 1 ? "" : "s"} per week.`}
          </p>
        </div>

        <div className="pt-2 border-t border-slate-100 flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid={D.availabilitySave}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-[13.5px] font-semibold transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={2.2} />}
            {saving ? "Saving…" : "Save availability"}
          </button>
        </div>
      </div>
    </motion.section>
  );
}

// -----------------------------------------------------------------------------
function ModeToggle({ testId, on, onChange, Icon, title, subtitle }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      data-testid={testId}
      aria-pressed={on}
      className={`text-left rounded-xl border p-4 flex items-start gap-3 transition-colors ${
        on ? "bg-[#2563EB]/5 border-[#2563EB]/40" : "bg-slate-50 border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${on ? "bg-[#2563EB] text-white" : "bg-white border border-slate-200 text-slate-500"}`}>
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13.5px] font-semibold text-slate-900">{title}</span>
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-full text-[10.5px] font-semibold ${
              on ? "bg-[#10B981]/15 text-[#059669]" : "bg-slate-200 text-slate-500"
            }`}
          >
            {on ? "ON" : "OFF"}
          </span>
        </div>
        <p className="mt-1 text-[12px] text-slate-500 leading-relaxed">{subtitle}</p>
      </div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">
        {label}
      </label>
      {children}
    </div>
  );
}

function HourSelect({ testId, value, onChange, min = 0, max = 23, disabled = false }) {
  const hours = [];
  for (let h = min; h <= max; h += 1) hours.push(h);
  return (
    <select
      data-testid={testId}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="h-9 rounded-lg bg-slate-50 border border-slate-200 px-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 disabled:cursor-not-allowed"
    >
      {hours.map((h) => (
        <option key={h} value={h}>{_fmtHour(h)}</option>
      ))}
    </select>
  );
}

function _fmtHour(h) {
  if (h === 0) return "12:00 AM";
  if (h === 12) return "12:00 PM";
  if (h === 24) return "12:00 AM (next)";
  return h < 12 ? `${h}:00 AM` : `${h - 12}:00 PM`;
}

function _hydrate(a) {
  return {
    online_available: !!a.online_available,
    in_person_available: !!a.in_person_available,
    session_duration_minutes: a.session_duration_minutes || 60,
    price_per_session: a.price_per_session || 0,
    timezone: a.timezone || "UTC",
    weekly_availability: [...(a.weekly_availability || [])].sort((x, y) => x.weekday - y.weekday),
  };
}
